import { debug } from '../../env/env';
import eventBus from '@refapp/eventbus-js';
import { inViewport, isVisible, throttle } from '../../utilities';
import { AnalyticsHandler } from '../AnalyticsHandler';
import createDataObjectBasedOnDom from './createDataObjectBasedOnDom';

if (
  'IntersectionObserver' in window &&
  'IntersectionObserverEntry' in window &&
  'intersectionRatio' in window.IntersectionObserverEntry.prototype &&
  !('isIntersecting' in IntersectionObserverEntry.prototype)
) {
  Object.defineProperty(window.IntersectionObserverEntry.prototype, 'isIntersecting', {
    get() {
      return this.intersectionRatio > 0;
    },
  });
}

// Helper functions
const isCurrentTarget = ({ target, currentTarget }) => target === currentTarget;

const shouldSkipEvent = (modifiers, event) => {
  return modifiers.target && event && !isCurrentTarget(event);
};

const prepareDataToSend = (node, data, modifiers) => {
  const dataToSend = modifiers.dom
    ? createDataObjectBasedOnDom(node.closest('[data-tag-wrapper]'), data)
    : data;

  if (modifiers.view) {
    dataToSend.viewPort = true;
  }

  return dataToSend;
};

const pushAnalytics = (dataToSend, registerOnly, node) => {
  try {
    AnalyticsHandler.getAnalyticsHandler().push(dataToSend, registerOnly);
  } catch (er) {
    if (debug) {
      node.setAttribute('data-error', er.message);
      node.classList.add('accessibilityError');
    }
    throw er;
  }
};

const createOnEventHandler = (node, data, modifiers) => {
  return (registerOnly = false, event) => {
    if (shouldSkipEvent(modifiers, event)) {
      return;
    }

    const dataToSend = prepareDataToSend(node, data, modifiers);
    pushAnalytics(dataToSend, registerOnly, node);
  };
};

const handleClickEvent = (node, onEvent) => {
  node.addEventListener('click', (event) => {
    onEvent(false, event);
  });
};

const handleConditionalEvent = (onEvent) => {
  onEvent(true);
  eventBus.on('conditions-met', onEvent);
};

const handleIntersectionChange = (change, onEvent, observer, node) => {
  if (change.isIntersecting) {
    onEvent();
    observer.unobserve(node);
  }
};

const handleIntersectionObserverEvent = (node, onEvent) => {
  const observer = new IntersectionObserver(
    (changes) => {
      changes.forEach((change) => {
        handleIntersectionChange(change, onEvent, observer, node);
      });
    },
    {
      threshold: [0.006],
    }
  );

  observer.observe(node);
};

const createScrollCallback = (node, onEvent) => {
  const scrollCallback = (event) => {
    /* istanbul ignore else */
    if (inViewport(node) && isVisible(node)) {
      onEvent(false, event);
      window.removeEventListener('scroll', throttle(scrollCallback, 500));
    }
  };
  return scrollCallback;
};

const handleScrollFallbackEvent = (node, onEvent) => {
  const scrollCallback = createScrollCallback(node, onEvent);
  window.addEventListener('scroll', throttle(scrollCallback, 500), false);
  scrollCallback();
};

/**
 * Binds the event for the Google TagManager action to the DOM node. Click or Impression.
 * @param {String} type Type of event. Can be click or impression.
 * @param {Node} node Dom node to use for event handling.
 * @param {Object} data Data send by the component who wants to send an analytics event.
 * @param {Object} modifiers Modifiers passed from directive
 */
export default function handleEvent(type, node, data, modifiers = {}) {
  const onEvent = createOnEventHandler(node, data, modifiers);

  if (type === 'click') {
    handleClickEvent(node, onEvent);
  } else if (type === 'conditional') {
    handleConditionalEvent(onEvent);
  } else if ('IntersectionObserver' in window) {
    handleIntersectionObserverEvent(node, onEvent);
  } else {
    handleScrollFallbackEvent(node, onEvent);
  }
}
